/**
 * "Add to calendar" for a congress: an iCalendar file the browser downloads and
 * the reader's calendar app opens. The congress is an all-day event over its
 * dates, read in UTC like `buildCongressDateLabel`, so the entry spans the days
 * the header prints.
 */
export type CongressCalendarEvent = {
  uid: string;
  title: string;
  startDate: string;
  endDate: string;
  location: string;
  url: string;
};

const DAY_MS = 86_400_000;

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

function utcDate(iso: string, offsetMs: number): string {
  const date = new Date(new Date(iso).getTime() + offsetMs);
  return `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}`;
}

function floatingDateTime(iso: string): string {
  const date = new Date(iso);
  return `${utcDate(iso, 0)}T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}00`;
}

// RFC 5545 §3.3.11: backslash, semicolon, comma and newline are escaped in TEXT.
function escapeText(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");
}

export function congressCalendarFile(
  event: CongressCalendarEvent,
  now: Date
): string {
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Juisci//Congress Space//EN",
    "BEGIN:VEVENT",
    `UID:${event.uid}@juisci.com`,
    `DTSTAMP:${floatingDateTime(now.toISOString())}Z`,
    // DTEND of an all-day event is EXCLUSIVE: the day after the last day.
    `DTSTART;VALUE=DATE:${utcDate(event.startDate, 0)}`,
    `DTEND;VALUE=DATE:${utcDate(event.endDate, DAY_MS)}`,
    `SUMMARY:${escapeText(event.title)}`,
    `LOCATION:${escapeText(event.location)}`,
    `URL:${event.url}`,
    "END:VEVENT",
    "END:VCALENDAR",
    "",
  ].join("\r\n");
}

export function downloadCongressCalendarFile(
  event: CongressCalendarEvent
): void {
  const blob = new Blob([congressCalendarFile(event, new Date())], {
    type: "text/calendar;charset=utf-8",
  });
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = `${event.uid}.ics`;
  link.click();
  URL.revokeObjectURL(href);
}
